import { useEffect, useState } from 'react';
import { CodeConfirmationForm } from '../../../../ui/components/CodeConfirmationForm';
import { Button } from '../../../../ui/components/Button/Button';
import { Checkbox } from '../../../../ui/components/Checkbox/Checkbox';
import { Icon } from '../../../../ui/icons/Icon';
import styles from './SignLoanPage.module.scss';
import { useUser } from '@/features/user/hooks/useUser';
import { writingHand } from '@/assets/images/emojis';
import { useLoan } from '../../hooks/useLoan';
import { routes } from '@/routes/routes';
import { useNavigate } from 'react-router-dom';
import { useApplication } from '@/features/application';
import { useLogger } from '@/lib/logger';
import { genericMessages, loanSignMessages } from '@/constants/errorMessages';
import { useError } from '@/providers/Error';

function SignLoanPage() {
  const navigate = useNavigate();
  const { error, setError, clearError } = useError();
  const logger = useLogger();
  const { user } = useUser();
  const [code, setCode] = useState('');
  const [isChecked, setIsChecked] = useState(false);
  const { applicationState, isApplicationStateLoading } = useApplication(true);
  const {
    requestContractSignCode,
    requestSignCodeError,
    isSignCodeRateLimited,
    signContractWithCode,
    signContractError,
    resendDelay,
    isLoading: isLoanLoading,
    downloadContract,
    isDownloadingContract,
  } = useLoan(true);

  const isLoading = isApplicationStateLoading || isLoanLoading;
  const isDisabled = isLoading || code.length !== 6 || !isChecked;

  useEffect(() => {
    if (requestSignCodeError || signContractError) {
      setError(
        loanSignMessages[
          signContractError?.message as keyof typeof loanSignMessages
        ] || genericMessages.unexpected
      );
    }
  }, [requestSignCodeError, signContractError]);

  useEffect(() => {
    if (isSignCodeRateLimited) {
      setError(loanSignMessages.otp_rate_limited);
    }
  }, [isSignCodeRateLimited]);

  const handleCodeChange = (value: string) => {
    setCode(value);
  };

  const handleResendCode = async () => {
    // Nothing sent: the resend stays available.
    if (!applicationState?.contractId || isLoading) return 0;
    clearError();
    const nextDelay = await requestContractSignCode(applicationState.contractId);
    setCode('');
    return nextDelay;
  };

  const handleDownloadContract = async () => {
    if (!applicationState?.contractId) return;

    // Open the tab immediately in response to the click
    // cover safari download popup
    const newTab = window.open('', '_blank');

    try {
      const contractLink = await downloadContract(applicationState.contractId);

      if (newTab) {
        newTab.location.href = contractLink;
      } else {
        window.location.href = contractLink;
      }
    } catch (err) {
      if (newTab) newTab.close();
      logger.error('Failed to download contract', {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const handleContinue = async () => {
    if (!applicationState?.contractId) return;
    if (isDisabled) return;

    // The checkbox: not politically exposed, and the real beneficiary.
    const result = await signContractWithCode(
      applicationState.contractId,
      code,
      { isRealBeneficiary: isChecked, isPoliticallyExposed: !isChecked }
    );

    if (result) {
      navigate(routes.loan.active, { replace: true });
      clearError();
      return;
    }

    setError(genericMessages.unexpected);
  };

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        {/* Header Section */}
        <div className={styles.header}>
          <div className={styles.emojiContainer}>
            <img
              src={writingHand}
              alt="Writing Hand"
              className={styles.emoji}
            />
          </div>
          <h1 className={styles.title}>Semnare contract</h1>
          <p className={styles.subtitle}>
            Confirmă codul trimis prin SMS către <br /> {user?.phone}
          </p>
        </div>

        {/* Main Content */}
        <div className={styles.main}>
          <CodeConfirmationForm
            value={code}
            onChange={handleCodeChange}
            onResendCode={handleResendCode}
            isLoading={isLoading}
            resendDelay={resendDelay}
            error={error}
            autoFocus
          />
        </div>

        <div className={styles.downloadContract}>
          <p className={styles.downloadContractTitle}>Contract de credit</p>
          <Button
            trackingId="sign_loan_download_contract"
            variant="text"
            size="xsmall"
            text="Descarcă"
            rightIcon={<Icon name="file-download" />}
            onClick={handleDownloadContract}
            loading={isDownloadingContract}
          />
        </div>
      </div>
      {/* Bottom Section */}
      <div className={styles.bottomSection}>
        <div className={styles.checkboxContainer}>
          <Checkbox
            checked={isChecked}
            onChange={(e) => setIsChecked(e.target.checked)}
            className={styles.checkbox}
          />
          <p
            className={styles.checkboxLabel}
            onClick={() => setIsChecked(!isChecked)}
          >
            Nu sunt o persoană expusă politic și sunt beneficiarul real al
            banilor
          </p>
        </div>

        <div className={styles.buttonContainer}>
          <Button
            trackingId="sign_loan_continue"
            onClick={handleContinue}
            loading={isLoading}
            rightIcon={<Icon name="arrow-right" />}
            className={styles.continueButton}
            disabled={isDisabled}
            text={'Continuă'}
          />
        </div>
      </div>
    </div>
  );
}

export default SignLoanPage;
